<template>
  <div class="sf-pad-t-3">
    <div class="sf-grid sf-grid-fluid">
      <div v-if="fetchingCommunicationsInProgress" class="sf-row">
        <div class="sf-col sf-align-c">
          <div class="sf-spinner sf-inline-block"></div>
        </div>
      </div>
      <div v-else class="sf-row">
        <div class="sf-col">
          <h3>{{ I18n.t('pulse.notes') }}</h3>
          <template v-if="showNotesEmptyState"
            ><div class="sf-align-c sf-below-3">
              <div
                class="sf-c-text-light"
                style="
                  display: inline-block;
                  height: 107.5px;
                  width: 107.5px;
                  background-color: #fff;
                  font-size: 45px;
                  border-radius: 50%;
                  line-height: 107.5px;
                "
              >
                <i class="i-no-notes"></i>
              </div></div></template
          ><template v-else
            ><remark-card
              v-for="remark in sortedVisibleNotes"
              :key="remark.id"
              :show-context="true"
              :show-context-btn="true"
              :show-period="true"
              v-bind="remark"
            ></remark-card
            ><template v-if="showingMutedNotes"
              ><remark-card
                v-for="remark in sortedMutedNotes"
                :key="remark.id"
                :show-context="true"
                :show-context-btn="true"
                :show-period="true"
                v-bind="remark"
              ></remark-card></template
          ></template>
          <div v-if="sortedMutedNotes.length && !showingMutedNotes">
            <div class="sf-align-c sf-below-4">
              <a class="sf-btn sf-btn-small" @click.prevent="toggleMutedNotes">{{
                I18n.t('communication.notes.toggle_muted', {
                  count: sortedMutedNotes.length,
                })
              }}</a>
            </div>
          </div>
        </div>
        <div class="sf-col">
          <h3>{{ I18n.t('pulse.todos') }}</h3>
          <template v-if="showTodosEmptyState"
            ><div class="sf-align-c sf-below-3">
              <div
                class="sf-c-text-light"
                style="
                  display: inline-block;
                  height: 107.5px;
                  width: 107.5px;
                  background-color: #fff;
                  font-size: 45px;
                  border-radius: 50%;
                  line-height: 107.5px;
                "
              >
                <i class="i-no-todos"></i>
              </div></div></template
          ><template v-else
            ><remark-card
              v-for="remark in sortedPendingTodos"
              :key="remark.id"
              :show-context="true"
              :show-context-btn="true"
              :show-period="true"
              v-bind="remark"
            ></remark-card
            ><check-list
              v-for="checkList in sortedPendingCheckLists"
              :key="checkList.id"
              ref="checkList"
              :show-context-btn="true"
              v-bind="checkList"
            ></check-list
            ><template v-if="showingCompletedTodosAndCheckLists"
              ><remark-card
                v-for="remark in sortedCompletedTodos"
                :key="remark.id"
                :show-context="true"
                :show-context-btn="true"
                :show-period="true"
                v-bind="remark"
              ></remark-card
              ><check-list
                v-for="checkList in sortedCompletedCheckLists"
                :key="checkList.id"
                ref="checkList"
                :show-context-btn="true"
                v-bind="checkList"
              ></check-list></template
          ></template>
          <div v-if="completedCount">
            <div class="sf-align-c sf-below-4">
              <a
                class="sf-btn sf-btn-small"
                @click.prevent="toggleCompletedTodosAndCheckLists"
                >{{
                  I18n.t(
                    'communication.todos.toggle_completed.' +
                      showingCompletedTodosAndCheckLists,
                    { count: completedCount }
                  )
                }}</a
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import $ from 'jquery'

import { mapActions, mapGetters, mapState } from 'vuex'

import scrollHelper from './mixins/scroll-helper'
import RemarkCard from './remarks/remark-card'
import CheckList from './checks/check-list'
import useCheckHelpers from '@app/application/communication/sections/communication/composables/use-check-helpers'

const PulseCommunication = {
  components: {
    RemarkCard,
    CheckList,
  },
  mixins: [scrollHelper],

  setup() {
    return {
      ...useCheckHelpers(),
    }
  },

  data() {
    return {
      // Storing this as local state, because only this component needs to know about it
      showingCompletedTodosAndCheckLists: false,
      showingMutedNotes: false,
      anchorElementScrolled: false,
    }
  },

  computed: {
    ...mapGetters([
      'fetchingCommunicationsInProgress',
      'notes',
      'todos',
      'checkListChecks',
    ]),

    ...mapState({
      checkLists: (state) => state.checks.checkLists,
      contextType: (state) => state.contextType,
      contextId: (state) => state.contextId,
      ledgerId: (state) => state.ledgerId,
      storeLedgerId: (state) => state.ledgerId,
      ledgerPeriodName: (state) => state.ledgerPeriodName,
    }),

    showNotesEmptyState() {
      return this.sortedNotes.length === 0
    },

    showTodosEmptyState() {
      let count =
        this.sortedPendingTodos.length + this.sortedPendingCheckLists.length
      if (this.showingCompletedTodosAndCheckLists) {
        count =
          count +
          this.sortedCompletedTodos.length +
          this.sortedCompletedCheckLists.length
      }
      return count === 0
    },

    completedCount() {
      return (
        this.sortedCompletedTodos.length + this.sortedCompletedCheckLists.length
      )
    },

    sortedNotes() {
      return _.orderBy(
        this.notes,
        ['sticky', 'lastCommentedAt'],
        ['desc', 'desc']
      )
    },

    sortedVisibleNotes() {
      const currentUserExternal = window.Silverfin.isCurrentUserExternal()

      if (currentUserExternal) {
        return this.sortedNotes.filter(
          (note) =>
            // if external user:
            // sticky or inside current ledger that user follows (following comes from scope)
            note.sticky || note.ledgerId === this.ledgerId
        )
      } else {
        // if internal user:
        // all that user follows (following comes from scope)
        return this.sortedNotes
      }
    },

    sortedMutedNotes() {
      return _.without(this.sortedNotes, ...this.sortedVisibleNotes)
    },

    sortedTodos() {
      return _.orderBy(
        this.todos,
        ['sticky', 'lastCommentedAt'],
        ['desc', 'desc']
      )
    },

    sortedCompletedTodos() {
      return this.sortedTodos.filter((todo) => todo.done)
    },

    sortedPendingTodos() {
      return this.sortedTodos.filter((todo) => !todo.done)
    },

    sortedCheckLists() {
      return _.orderBy(this.checkLists, ['text'], ['asc'])
    },

    sortedCompletedCheckLists() {
      return this.sortedCheckLists.filter((checkList) =>
        this.checkListChecks(this.ledgerId, checkList.id).every((check) => {
          return this.checkCheckedByAllParticipants(check)
        })
      )
    },

    sortedPendingCheckLists() {
      return _.without(this.sortedCheckLists, ...this.sortedCompletedCheckLists)
    },

    checkIds() {
      return _.flatMap(this.$refs.checkList, (checkList) => checkList.checkIds)
    },
  },

  created() {
    this.fetchCommunication()
    this.fetchMentionUsernames()
    // Event handlers
    window.eventHub.$on('refreshCommunicationData', this.fetchCommunication)
    window.eventHub.$on('resetCommunicationData', this.resetCommunicationData)
  },

  beforeUnmount() {
    window.eventHub.$off('refreshCommunicationData', this.fetchCommunication)
    window.eventHub.$off('resetCommunicationData', this.resetCommunicationData)
  },

  methods: {
    ...mapActions([
      'fetchCommunication',
      'fetchMentionUsernames',
      'resetCommunicationData',
    ]),

    toggleCompletedTodosAndCheckLists() {
      this.showingCompletedTodosAndCheckLists =
        !this.showingCompletedTodosAndCheckLists
    },

    toggleMutedNotes() {
      this.showingMutedNotes = !this.showingMutedNotes
    },

    handleAnchorScroll() {
      this.scrollTo(this.elementAnchorLink()).then(() => {
        this.anchorElementScrolled = true
      })
    },

    expandHiddenItems() {
      const id = this.elementAnchorLink().split('-')[1]

      return Promise.resolve().then(() => {
        const anchorElement = $(this.$el).find(this.elementAnchorLink())
        // Return if element already exists among the pending items
        if (anchorElement.is(':visible')) {
          return
        }

        // Toggle completed todos section if anchored element lives there
        else if (this.sortedCompletedTodos.find((c) => c.id === id)) {
          this.showingCompletedTodosAndCheckLists = true
        }

        // Toggle completed checks in checklists section if anchored element lives there
        // This expands multi level completed checks(partially and fully complete checklists) as required
        else if (this.checkIds.find((c) => c === id)) {
          _.forEach(this.$refs.checkList, (checkList) => {
            if (checkList.completedCheckIds.find((c) => c === id)) {
              checkList.toggleCompletedChecks()
            }
          })
        }
      })
    },
  },
}

export default PulseCommunication
</script>
